import React from 'react';
import {
  Badge,
  Box,
  HStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { SaktiPushOdooJob, SaktiPushOdooResult } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

interface SaktiPushOdooResultPanelProps {
  job: SaktiPushOdooJob | null;
  result: SaktiPushOdooResult | null;
  isSubmitting: boolean;
}

const ENTITY_LABELS: Record<string, string> = {
  rkh: 'RKH',
  transport: 'Transport',
  harvest: 'Harvest',
  bkm: 'BKM',
  official_report: 'Official Report',
};

const stateBadge = (status: string | undefined) => {
  switch (status) {
    case 'completed':
      return { label: 'Selesai', color: 'green' };
    case 'failed':
      return { label: 'Gagal', color: 'red' };
    case 'running':
      return { label: 'Sedang berjalan', color: 'blue' };
    default:
      return { label: 'Dalam antrean', color: 'gray' };
  }
};

const SaktiPushOdooResultPanel: React.FC<SaktiPushOdooResultPanelProps> = ({
  job,
  result,
  isSubmitting,
}) => {
  if (!job && !isSubmitting) {
    return (
      <Box
        bg="white"
        borderRadius={SAKTI_UI_SIZE.cardRadius}
        p={SAKTI_UI_SIZE.cardPadding}
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Text fontSize="sm" color="gray.500">
          Pilih estate dan rentang tanggal, lalu jalankan sync. Hasil push per entity akan tampil di sini.
        </Text>
      </Box>
    );
  }

  const badge = stateBadge(job?.status);
  const running = isSubmitting || (!!job && job.status !== 'completed' && job.status !== 'failed');

  return (
    <Box
      bg="white"
      borderRadius={SAKTI_UI_SIZE.cardRadius}
      p={SAKTI_UI_SIZE.cardPadding}
      borderWidth="1px"
      borderColor="gray.200"
    >
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between" align="center">
          <Text fontWeight="semibold" color="gray.800">
            Status Sync
          </Text>
          <HStack spacing={2}>
            {running && <Spinner size="sm" color="customGreen.500" />}
            <Badge colorScheme={badge.color}>{badge.label}</Badge>
          </HStack>
        </HStack>

        {job?.status === 'failed' && (
          <Text fontSize="sm" color="red.600">
            {job.error || 'Terjadi kesalahan saat push ke Odoo.'}
          </Text>
        )}

        {result && (
          <>
            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
              <Stat>
                <StatLabel fontSize={SAKTI_UI_SIZE.kpiLabel} color="gray.500">
                  Total Tersinkron
                </StatLabel>
                <StatNumber color="green.600">
                  {result.total_synced.toLocaleString('id-ID')}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize={SAKTI_UI_SIZE.kpiLabel} color="gray.500">
                  Total Gagal
                </StatLabel>
                <StatNumber color={result.total_failed > 0 ? 'red.600' : 'gray.700'}>
                  {result.total_failed.toLocaleString('id-ID')}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize={SAKTI_UI_SIZE.kpiLabel} color="gray.500">
                  Durasi
                </StatLabel>
                <StatNumber fontSize="lg" color="gray.700">
                  {result.duration}
                </StatNumber>
              </Stat>
            </SimpleGrid>

            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Entity</Th>
                    <Th isNumeric>Tersinkron</Th>
                    <Th isNumeric>Gagal</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {Object.entries(result.results).map(([key, entity]) => (
                    <Tr key={key}>
                      <Td>{ENTITY_LABELS[key] ?? key}</Td>
                      <Td isNumeric>{entity.synced.toLocaleString('id-ID')}</Td>
                      <Td isNumeric color={entity.failed > 0 ? 'red.600' : undefined}>
                        {entity.failed.toLocaleString('id-ID')}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {Object.entries(result.results).some(([, e]) => e.errors.length > 0) && (
              <Box>
                <Text fontWeight="semibold" fontSize="sm" color="red.600" mb={2}>
                  Detail Error
                </Text>
                <VStack align="stretch" spacing={1}>
                  {Object.entries(result.results).flatMap(([key, entity]) =>
                    entity.errors.map((err, idx) => (
                      <Text key={`${key}-${idx}`} fontSize="xs" color="gray.600">
                        <Badge mr={2} colorScheme="red" variant="subtle">
                          {ENTITY_LABELS[key] ?? key}
                        </Badge>
                        {err.id}: {err.error}
                      </Text>
                    ))
                  )}
                </VStack>
              </Box>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
};

export default SaktiPushOdooResultPanel;
